import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { account } from "../lib/api.js";
import { useAuth } from "../lib/auth-context.js";

/**
 * Change your own password.
 *
 * Doubles as the forced-change screen: an account created by an admin lands
 * here and the API refuses everything else until it is done, so the copy has
 * to explain why rather than looking like an arbitrary interruption.
 */

const MIN_LENGTH = 12;

export function AccountPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const forced = user?.mustChangePassword ?? false;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // Checked here as well as server-side purely so the mismatch is caught
    // before a round trip; the server is still the authority on length.
    if (next !== confirm) {
      setError("The new passwords do not match");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await account.changePassword(current, next);
      // The session's mustChangePassword flag is stale until this reloads,
      // and it is what gates the rest of the app.
      await refresh();
      setDone(true);
      if (forced) navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 420 }}>
      <h1 style={{ fontSize: 15, margin: "0 0 16px", letterSpacing: "0.14em" }}>
        &gt; {forced ? "SET YOUR PASSWORD" : "CHANGE PASSWORD"}
      </h1>

      {forced && (
        <p
          style={{
            color: "var(--status-warning)",
            fontSize: 13,
            lineHeight: 1.5,
            border: "1px solid var(--status-warning)",
            background: "color-mix(in srgb, var(--status-warning) 12%, var(--surface-1))",
            padding: "10px 12px",
            marginTop: 0,
          }}
        >
          This account was created with a temporary password, which means someone
          else has seen it. Choose your own before continuing.
        </p>
      )}

      {done && !forced && (
        <p style={{ color: "var(--success-text)", fontSize: 13 }}>
          PASSWORD CHANGED. Other sessions have been signed out.
        </p>
      )}

      {error && (
        <p role="alert" style={{ color: "var(--status-critical)", fontSize: 13 }}>
          {`!! ${error}`}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <label style={labelStyle}>&gt; {forced ? "TEMPORARY PASSWORD" : "CURRENT PASSWORD"}</label>
        <input
          type="password"
          required
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          style={inputStyle}
        />

        <label style={{ ...labelStyle, marginTop: 14 }}>&gt; NEW PASSWORD</label>
        <input
          type="password"
          required
          minLength={MIN_LENGTH}
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          style={inputStyle}
        />
        <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "5px 0 0" }}>
          {/* Length only. Composition rules push people toward "Password1!"
              and predictable mutations, which is worse on every axis. */}
          {`MINIMUM ${MIN_LENGTH} CHARACTERS. A PHRASE BEATS A SHORT COMPLEX ONE.`}
        </p>

        <label style={{ ...labelStyle, marginTop: 14 }}>&gt; CONFIRM NEW PASSWORD</label>
        <input
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={inputStyle}
        />

        <button type="submit" disabled={saving} style={buttonStyle}>
          {saving ? "[ SAVING... ]" : "[ SET PASSWORD ]"}
        </button>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid var(--border)",
  background: "var(--surface-raised)",
  color: "var(--text-primary)",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "var(--text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.08em",
};

const buttonStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 20,
  padding: "9px 0",
  fontSize: 13,
  letterSpacing: "0.12em",
  color: "var(--page-plane)",
  background: "var(--text-primary)",
  border: "1px solid var(--text-primary)",
};
